import { Box } from '@chakra-ui/react';
import React from 'react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import * as yup from 'yup';
import { ArticleHeader } from '../../components/ArticlesGrid';
import { getGaCid } from '../../server/libs/googleAnalytics';
import withAuth from '../../server/libs/withAuth';
import {
  getSpecialArticleProps,
  getSpecialArticleSettings,
} from '../../server/libs/specialArticles';
import createUserAccessHistory from '../../server/libs/createUserAccessHistory';
import {
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isLicenseVerified as isLicenseVerifiedFunc,
} from '../../common/libs/user';
import { Tag } from '../tagsearch/[tagIds]';
import { AntaaAccountType } from '../../common/libs/antaaAccountType';
import { hasDemoAuthority } from '../../common/libs/userRoleType';
import {
  findTagsByNames,
  getArticleIdsWithTagIds,
} from '../../server/libs/tag';
import useSpecialPage from '../../hooks/useSpecialPage';
import {
  SlidePostBoostContent,
  SlidePostBoostContentProps,
} from '../../components/SlidePostBoostContent';

import articlesInSpecialPage from '../../server/libs/articlesInSpecialPage';
import firstPublishedPeriodArticles from '../../server/libs/firstPublishedPeriodArticles';
import {
  CompilationPageContent,
  CompilationContentProps,
} from '../../components/CompilationPageContent';
import logger from '../../server/libs/logger';
import { generateS3DownloadUrl } from '../../server/libs/siteSettings';

type CommonProps = {
  specialPageTitle: string;
  mainImagePath: string;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isDemoAccount: boolean;
  isAllowAccount: boolean; // ホワイトリストに含まれるアカウントかどうか
  isPrivate: boolean;
};

const Content = ({
  specialArticleId,
  ...restProps
}: SlidePostBoostContentProps | CompilationContentProps) => {
  const templateName = restProps.templateName;

  switch (templateName) {
    case 'post-boost':
      return (
        <SlidePostBoostContent
          specialArticleId={specialArticleId}
          {...restProps}
        />
      );
    case 'compilation':
      return (
        <CompilationPageContent
          specialArticleId={specialArticleId}
          {...restProps}
        />
      );
    default:
      // ServerSidePropsではtemplateNameが不正な場合404を返すためここに到達することはない
      return null;
  }
};

type PageContext = CommonProps &
  (SlidePostBoostContentProps | CompilationContentProps);
const SpecialPage: NextPage<PageContext> = ({
  specialArticleId,
  specialPageTitle,
  mainImagePath,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isAllowAccount,
  isPrivate,
  ...restProps
}) => {
  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialArticleId,
    specialPagePath: `/special/${specialArticleId}`,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    isAllowAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
      </Box>
      <Content
        specialArticleId={specialArticleId}
        isAllowAccount={isAllowAccount}
        {...restProps}
      />
    </>
  );
};

export default SpecialPage;

export const getServerSideProps: GetServerSideProps<PageContext> =
  withAuth<PageContext>(async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';
    const isAuthenticated = context.req.isAuthenticated();

    const specialArticleId = yup
      .string()
      .validateSync(context.params?.articleId);
    if (!specialArticleId) {
      return {
        notFound: true,
      };
    }

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: specialArticleId.replace(/-/g, '').slice(0, 16), // user_access_histories.operationカラムの値のため合計16文字以内
    });

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isLicenseVerified = isLicenseVerifiedFunc(context.req.user ?? null);
    const isLicenseVerifiedDoctor =
      context.req.user?.antaaAccountType === AntaaAccountType.DOCTOR &&
      isLicenseVerified;

    const [
      specialArticleProps,
      settings, // SlidePostBoostSettings| CompilationSettings
    ] = await Promise.all([
      getSpecialArticleProps(specialArticleId, userId),
      getSpecialArticleSettings(specialArticleId),
    ]);

    const {
      isPrivate,
      isAllowAccount,
      canDisplay,
      title,
      topImage,
      externalUrl,
    } = specialArticleProps;

    if (
      !specialArticleId ||
      !canDisplay ||
      !title ||
      !settings ||
      !!externalUrl
    ) {
      return {
        notFound: true,
      };
    }

    let articleIdList: string[] = [];

    if (settings.templateName === 'post-boost') {
      // ここからスライドデータ取得
      const targetTags = await findTagsByNames(settings?.articles?.tags ?? []);
      const tags: Tag[] = targetTags.map((tag) => ({
        id: tag.tag_id,
        name: tag.tag_name,
      }));
      let tagArticleIdList: string[] = [];

      if (tags.length) {
        tagArticleIdList = await getArticleIdsWithTagIds(
          tags.map((tag) => tag.id),
        );
      }

      // 認証済み医師アカウントのみ企業スライドのarticleIdを配列に追加
      const corporationArticleIds =
        (isAllowAccount || isLicenseVerifiedDoctor) &&
        settings?.corporationArticles?.articleIds?.length
          ? settings.corporationArticles?.articleIds
          : [];

      // 必要なarticleIdをマージ
      articleIdList = [
        ...articleIdList,
        ...tagArticleIdList,
        ...settings.articles.articleIds,
        ...corporationArticleIds,
      ];
    }

    if (settings.templateName === 'compilation') {
      // categoriesの中にarticleIdsがあればflatな配列にする
      articleIdList = settings.compilation.chapters.flatMap(
        (chapter) => chapter.articleIds ?? [],
      );
    }

    // articleIdListの重複を排除
    const uniqueArticleIdList = [...new Set(articleIdList)];

    // DBからスライド取得
    let articles: ArticleHeader[] = [];
    if (
      settings.templateName === 'post-boost' &&
      settings.articles.firstPublishedPeriod
    ) {
      const start = new Date(settings.articles.firstPublishedPeriod.start);
      const end = settings.articles.firstPublishedPeriod?.end
        ? new Date(settings.articles.firstPublishedPeriod.end)
        : undefined;
      if (
        !Number.isNaN(start.getTime()) &&
        (end === undefined || !Number.isNaN(end.getTime()))
      ) {
        articles = await firstPublishedPeriodArticles({
          isAuthenticated,
          isAllowAccount,
          user: context.req.user,
          firstPublishedPeriodDate: {
            start,
            end,
          },
        });
      } else {
        logger.error(
          `スライドの期間指定の値が不正です; start=${
            settings.articles.firstPublishedPeriod.start
          }, end=${settings.articles.firstPublishedPeriod.end ?? 'undefined'}`,
        );
      }
    } else {
      articles = await articlesInSpecialPage({
        articleIdList: uniqueArticleIdList,
        isAuthenticated,
        isAllowAccount,
        user: context.req.user,
      });
    }

    // post-boostではarticleIdをマージして取得した結果を再び振り分ける
    if (settings.templateName === 'post-boost') {
      if (settings.articles.firstPublishedPeriod) {
        settings.articles.articles = articles;
      } else if (articleIdList.length) {
        if (
          (isAllowAccount || isLicenseVerifiedDoctor) &&
          settings?.corporationArticles?.articleIds.length &&
          settings?.articles
        ) {
          // 配列初期化
          settings.corporationArticles.articles = [];
          settings.articles.articles = [];
          articles.forEach((article) => {
            if (
              settings?.corporationArticles?.articleIds.includes(
                article.articleId,
              )
            ) {
              settings.corporationArticles.articles?.push(article);
            } else {
              settings.articles.articles?.push(article);
            }
          });
        } else if (
          (isAllowAccount || isLicenseVerifiedDoctor) &&
          settings?.corporationArticles
        ) {
          settings.corporationArticles.articles = articles;
        } else if (settings?.articles) {
          settings.articles.articles = articles;
        }
      }
      if (
        !!settings?.articles?.articles?.length &&
        settings.articles?.sortOrder
      ) {
        settings.articles.articles = [...settings.articles.articles].sort(
          (a, b) => {
            switch (settings.articles.sortOrder) {
              case 'SessionCountDesc':
                return b.sessionCount - a.sessionCount;
              case 'LikeCountDesc':
                return b.likeCount - a.likeCount;
              case 'UpdateDateDesc':
                return Date.parse(b.updateDate) - Date.parse(a.updateDate);
              default:
                return 0;
            }
          },
        );
      }
    }

    const getTemplateProps = () => {
      switch (settings.templateName) {
        case 'post-boost':
          return {
            ...settings,
            isMobile,
            specialArticleId,
            isLicenseVerifiedDoctor,
          };
        case 'compilation':
          return {
            ...settings,
            articles,
            isMobile,
            specialArticleId,
            isLicenseVerifiedDoctor,
          };
        default:
          return null;
      }
    };

    const templateProps = getTemplateProps();

    if (!templateProps) {
      return {
        notFound: true,
      };
    }

    const commonProps: CommonProps = {
      specialPageTitle: title,
      mainImagePath: topImage?.includes('/')
        ? generateS3DownloadUrl(topImage)
        : `/images/special/${topImage}`,
      isAuthenticated,
      isLicenseVerified,
      isCorporationAccount:
        context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
      isPublisherAccount: isPublisherAccount(context.req.user),
      isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
      isDemoAccount: hasDemoAuthority(context.req.user?.userRoles),
      isAllowAccount,
      isPrivate,
    };

    return {
      props: {
        ...commonProps,
        ...templateProps,
      },
    };
  });
